import React, { useState } from 'react';

interface APISettingsProps {
  onSave: (settings: any) => Promise<void>;
  isSaving: boolean;
}

const APISettings: React.FC<APISettingsProps> = ({ onSave, isSaving }) => {
  const [formData, setFormData] = useState({
    enableAPI: true,
    restrictIPs: false,
    allowedIPs: '',
    rateLimit: 100,
  });

  // Mock API key for demonstration
  const apiKey = 'sk_live_12345abcdefghijklmnopqrstuvwxyz';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  const handleGenerateNewKey = () => {
    // In a real app, this would call an API to generate a new key
    alert('This would generate a new API key and invalidate the old one.');
  };

  return (
    <div className="space-y-6">
      {/* API Status */}
      <div className="bg-white shadow-sm rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">API Access</h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>
              Your API key grants access to your data. Keep it secure and never share it publicly.
            </p>
          </div>
          <div className="mt-5 sm:flex sm:items-center">
            <div className="max-w-xs w-full">
              <label htmlFor="apiKey" className="sr-only">
                API Key
              </label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="text"
                  name="apiKey"
                  id="apiKey"
                  value={apiKey}
                  readOnly
                  className="focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50"
                />
              </div>
            </div>
            <span className="mt-3 sm:mt-0 sm:ml-3">
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(apiKey)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                Copy
              </button>
            </span>
            <span className="mt-3 sm:mt-0 sm:ml-3">
              <button
                type="button"
                onClick={handleGenerateNewKey}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
              >
                Generate New Key
              </button>
            </span>
          </div>
        </div>
      </div>
      
      {/* API Settings */}
      <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">API Settings</h3>
          <p className="mt-1 text-sm text-gray-500">
            Configure API access and security settings.
          </p>
          
          <div className="mt-6 space-y-6">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="enableAPI"
                  name="enableAPI"
                  type="checkbox"
                  checked={formData.enableAPI}
                  onChange={handleInputChange}
                  className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="enableAPI" className="font-medium text-gray-700">
                  Enable API Access
                </label>
                <p className="text-gray-500">
                  Allow external applications to access your data via the API.
                </p>
              </div>
            </div>
            
            {formData.enableAPI && (
              <>
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="restrictIPs"
                      name="restrictIPs"
                      type="checkbox"
                      checked={formData.restrictIPs}
                      onChange={handleInputChange}
                      className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="restrictIPs" className="font-medium text-gray-700">
                      Restrict API Access by IP
                    </label>
                    <p className="text-gray-500">
                      Only allow API requests from specific IP addresses.
                    </p>
                  </div>
                </div>
                
                {formData.restrictIPs && (
                  <div>
                    <label htmlFor="allowedIPs" className="block text-sm font-medium text-gray-700">
                      Allowed IP Addresses
                    </label>
                    <div className="mt-1">
                      <textarea
                        name="allowedIPs"
                        id="allowedIPs"
                        rows={3}
                        value={formData.allowedIPs}
                        onChange={handleInputChange}
                        placeholder="Enter one IP address per line"
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      Enter one IP address per line. Leave empty to allow all IPs.
                    </p>
                  </div>
                )}
                
                <div className="sm:col-span-3">
                  <label htmlFor="rateLimit" className="block text-sm font-medium text-gray-700">
                    Rate Limit (requests per minute)
                  </label>
                  <div className="mt-1">
                    <input
                      type="number"
                      name="rateLimit"
                      id="rateLimit"
                      min={10}
                      max={1000}
                      value={formData.rateLimit}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
      
      {/* Webhooks */}
      <div className="bg-white shadow-sm rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Webhooks</h3>
          <p className="mt-1 text-sm text-gray-500">
            Receive real-time notifications of events via webhooks.
          </p>
          
          <div className="mt-4">
            <a
              href="#"
              className="text-primary-600 hover:text-primary-500"
            >
              Configure Webhooks →
            </a>
          </div>
        </div>
      </div>
      
      {/* API Documentation */}
      <div className="bg-white shadow-sm rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">API Documentation</h3>
          <p className="mt-1 text-sm text-gray-500">
            Learn how to integrate with our API.
          </p>
          
          <div className="mt-4">
            <a
              href="#"
              className="text-primary-600 hover:text-primary-500"
            >
              View Documentation →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APISettings;
